import { Popover as PopoverPrimitive } from 'radix-ui';
import type * as React from 'react';
import { useKeyboardInset } from '@/lib/software-keyboard';
import { cn } from '@/lib/utils';

/** Radix's own shape: one number for every side, or a number per side. */
type CollisionPadding = NonNullable<
  React.ComponentProps<typeof PopoverPrimitive.Content>['collisionPadding']
>;

/**
 * A caller's collision padding with the software keyboard folded into it.
 *
 * The keyboard only ever pushes the bottom edge up, so it takes the larger of
 * the two: a caller asking for more room below already clears it. A single
 * number means all four sides, and is expanded before the merge so the other
 * three survive. Nothing to merge and nothing asked for gives `undefined`
 * rather than `{ bottom: 0 }` — on a desktop the prop should not reach Radix
 * at all, so this changes nothing where there is no keyboard.
 */
export function mergeCollisionPadding(
  caller: CollisionPadding | undefined,
  inset: number,
): CollisionPadding | undefined {
  if (caller === undefined && inset === 0) return undefined;
  const sides =
    typeof caller === 'number'
      ? { top: caller, right: caller, bottom: caller, left: caller }
      : { ...caller };
  return { ...sides, bottom: Math.max(sides.bottom ?? 0, inset) };
}

function Popover({ ...props }: React.ComponentProps<typeof PopoverPrimitive.Root>) {
  return <PopoverPrimitive.Root data-slot="popover" {...props} />;
}

function PopoverTrigger({ ...props }: React.ComponentProps<typeof PopoverPrimitive.Trigger>) {
  return <PopoverPrimitive.Trigger data-slot="popover-trigger" {...props} />;
}

function PopoverAnchor({ ...props }: React.ComponentProps<typeof PopoverPrimitive.Anchor>) {
  return <PopoverPrimitive.Anchor data-slot="popover-anchor" {...props} />;
}

function PopoverContent({
  className,
  align = 'center',
  sideOffset = 6,
  collisionPadding,
  ...props
}: React.ComponentProps<typeof PopoverPrimitive.Content>) {
  // Radix's collision machinery is the whole of the keyboard fix here: given
  // the hidden strip as padding below, floating-ui shifts or flips the popover
  // into what is still visible, and — the part the pickers depend on — writes
  // the room left over into `--radix-popover-content-available-height`.
  const inset = useKeyboardInset();
  // Spread rather than pass, so that with no keyboard and no caller value the
  // prop is genuinely absent and Radix keeps its own default.
  const padding = mergeCollisionPadding(collisionPadding, inset);

  return (
    <PopoverPrimitive.Portal>
      <PopoverPrimitive.Content
        data-slot="popover-content"
        align={align}
        sideOffset={sideOffset}
        {...(padding === undefined ? {} : { collisionPadding: padding })}
        className={cn(
          'z-50 w-72 origin-(--radix-popover-content-transform-origin) rounded-md border bg-popover p-4 text-popover-foreground shadow-md outline-hidden',
          'data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:zoom-in-95',
          'data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95',
          'data-[side=bottom]:slide-in-from-top-2 data-[side=top]:slide-in-from-bottom-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2',
          className,
        )}
        {...props}
      />
    </PopoverPrimitive.Portal>
  );
}

export { Popover, PopoverAnchor, PopoverContent, PopoverTrigger };
